/**
 * i18n Configuration
 * 
 * Configures i18next with language detection, fallback, and lazy loading support.
 * Supports 21 languages across 3 tiers with on-demand namespace loading.
 * 
 * CRITICAL: When language changes, all active namespaces are automatically
 * loaded for the new language before the UI re-renders.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { getLanguageCodes } from './languages';

/** All namespaces used in the app — auto-generated from en/ locale directory */
const ALL_NAMESPACES = [
  'common', 'auth', 'customers', 'bookings', 'companies', 'dashboard', 'forms', 'errors',
  '__root', '_authenticated', '$companyId',
  'accessControlTab', 'adminPage', 'agentPanel', 'aIConfirmModal', 'aICopilot',
  'aIOptimizationPanel', 'aPITester', 'app', 'audioPlayer', 'authLayout',
  'bookingNew', 'bookingSlotSelector', 'breadcrumbs', 'businessHoursConfig',
  'callAnalytics', 'capacityPlans', 'companyCard', 'companyDetail',
  'conflictResolutionModal', 'createDialog', 'customerDetail', 'customerForm',
  'customerNew', 'customersVirtualized', 'customSlotApprovalModal', 'customSlotRequests',
  'dashboardLayout', 'dataMigrationTab', 'dateRangeFilter',
  'dispatchBoard', 'dispatchFilters', 'dispatchMapView', 'dispatchProposalModal',
  'documentation', 'entityManager', 'errorBoundary', 'filterChips',
  'globalBanner', 'globalOperationTracker', 'inboxItemModal',
  'invoiceDetail', 'invoices', 'ivrBuilder', 'jobAttachments', 'jobChecklist',
  'jobDetail', 'jobPricing', 'jobs', 'languageSelector', 'leads',
  'liveCallPanel', 'locationSearch', 'login', 'main',
  'navigationBar', 'newInvoice', 'notificationPanel', 'officeActions',
  'operationalMetricsCards', 'operationStatusModal', 'parts',
  'permissionBadge', 'permissionGuard', 'phoneDashboard', 'privacySettings',
  'roleBadge', 'rootLayout', 'searchInput', 'searchPalette', 'settings',
  'sLAWarningModal', 'slotReservations', 'slotTemplateForm', 'slotTemplates',
  'softphone', 'sortBar', 'stateBadge', 'stateChangeConfirmModal',
  'systemLogsViewer', 'systemTab', 'tasks', 'teamTab', 'technicianLane',
  'timeline', 'timesheets', 'unassignedJobsPanel', 'undoModal',
  'uploadWidget', 'userMenu', 'voicemailTab',
  // Service OS Phase 2
  'animatedListExample', 'appointmentDetail', 'assetDetail', 'caseDetail', 'caseList',
  'collectionsDetail', 'coverageClaimDetail', 'disputeDetail', 'estimateDetail',
  'inventoryMovements', 'mobileOptimizationTest', 'partRequestDetail',
  'releaseManagement', 'responsiveTest', 'reviewTaskWorkspace', 'riskAssessments',
  'skipLinks', 'workerProfileDetail',
];

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: getLanguageCodes(),
    debug: Boolean(import.meta.env.DEV),

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    interpolation: {
      escapeValue: false,
    },

    react: {
      useSuspense: false, // We handle loading ourselves to avoid flash
    },

    resources: {},

    ns: ALL_NAMESPACES,
    defaultNS: 'common',

    partialBundledLanguages: true,
  });

/**
 * Load a single translation namespace dynamically
 */
export async function loadNamespace(lng: string, ns: string) {
  if (i18n.hasResourceBundle(lng, ns)) {
    return;
  }

  try {
    const translations = await import(`./locales/${lng}/${ns}.json`);
    i18n.addResourceBundle(lng, ns, translations.default, true, true);
  } catch {
    // Fallback: load English if the requested locale doesn't have this namespace
    if (lng !== 'en') {
      try {
        const enBundle = await import(`./locales/en/${ns}.json`);
        i18n.addResourceBundle(lng, ns, enBundle.default, true, true);
      } catch {
        console.warn(`Failed to load translation namespace: ${lng}/${ns}`);
      }
    }
  }
}

/**
 * Load ALL namespaces for a language.
 * Called automatically on language change.
 */
export async function loadAllNamespaces(lng: string) {
  await Promise.all(ALL_NAMESPACES.map(ns => loadNamespace(lng, ns)));
}

/**
 * Preload translation namespaces for a route
 */
export async function preloadTranslations(lng: string, namespaces: string[]) {
  await Promise.all(namespaces.map(ns => loadNamespace(lng, ns)));
}

/**
 * Change language with guaranteed namespace loading.
 * This is the ONLY function that should be used to switch languages.
 * It loads all namespaces BEFORE telling i18next to switch,
 * ensuring the UI never shows missing keys.
 */
export async function changeAppLanguage(lng: string) {
  // Load all namespaces for the target language first
  await loadAllNamespaces(lng);
  // Now switch — UI will re-render with translations already available
  await i18n.changeLanguage(lng);
  // Update HTML lang attribute
  document.documentElement.lang = lng;
}

// Also auto-load on any programmatic language change (safety net)
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
  void loadAllNamespaces(lng);
});

export default i18n;
